import { HorizontalSection } from '@/components/display/horizontal-section';
import { SearchBar } from '@/components/forms/search-bar';
import { BROWSE_MAX_WIDTH, centeredContent } from '@/components/layout/content-width';
import { ScreenHeader } from '@/components/layout/screen-header';
import { TAB_BOTTOM_SPACING, TabScreenWrapper } from '@/components/layout/tab-screen-wrapper';
import CuisineChip from '@/components/ui/cuisine-chip';
import CuisineChipSkeleton from '@/components/ui/cuisine-chip-skeleton';
import { ProgressiveChecklist } from '@/components/ui/progressive-checklist';
import { useTourTargets } from '@/features/feature-tours';
import { SuggestedFriends } from '@/features/friends/components/suggested-friends';
import { ExistingUserTastePrompt } from '@/features/onboarding/components/existing-user-taste-prompt';
import { useExistingUserPrompt } from '@/features/onboarding/hooks/use-existing-user-prompt';
import { useProgressiveChecklist } from '@/features/onboarding/hooks/use-progressive-checklist';
import { useUserLocation } from '@/hooks/use-user-location';
import { useSetting } from '@/features/settings';
import { router } from 'expo-router';
import React from 'react';
import { ScrollView, View } from 'react-native';
import { DiscoverEmptyState } from './components/empty-state';
import { RestaurantSection } from './components/restaurant-section';
import { SearchTypeahead } from './components/search-typeahead';
import { useDiscoverActions, useDiscoverData, useDiscoverSearch } from './hooks';

const CUISINE_SKELETON_KEYS = ['c1', 'c2', 'c3', 'c4', 'c5', 'c6'];

export function DiscoverScreen() {
  const { value: units } = useSetting('units');
  const { coords } = useUserLocation();
  const { registerTarget } = useTourTargets();
  const checklist = useProgressiveChecklist();
  const existingUserPrompt = useExistingUserPrompt();

  // Gate Nearby on a real fix rather than the default map centre. Querying the
  // fallback would fetch -- and, now that Nearby is persisted, cache -- places
  // far from the user and present them as nearby. 0/0 keeps the query disabled
  // (businessesNearbyQueryOptions' enabled gate) until a fix arrives, at which
  // point the Nearby section appears; RestaurantSection hides while empty.
  const nearbyLat = coords?.latitude ?? 0;
  const nearbyLng = coords?.longitude ?? 0;

  const data = useDiscoverData({ latitude: nearbyLat, longitude: nearbyLng });
  const search = useDiscoverSearch();
  const actions = useDiscoverActions({
    searchQuery: search.searchQuery,
    debouncedQuery: search.debouncedQuery,
    setIsSearchFocused: search.setIsSearchFocused,
  });

  return (
    <TabScreenWrapper>
      <View style={{ flex: 1 }}>
        {/* Centered browse column: clamps the stretched phone column on wide
            web / tablets. The typeahead lives inside so its absolute overlay
            aligns to the same column. */}
        <View style={{ flex: 1, ...centeredContent(BROWSE_MAX_WIDTH) }}>
        <ScrollView
          showsVerticalScrollIndicator={false}
          className="flex-1"
          contentContainerStyle={{ paddingBottom: TAB_BOTTOM_SPACING }}
        >
          <ScreenHeader
            title="Discover"
            subtitle="Find new places and dishes to try"
            showMenuButton
          />

          <View
            className="px-4 relative"
            ref={(ref) => {
              (search.searchBarRef as React.MutableRefObject<View | null>).current = ref;
              registerTarget('discover-search-bar', ref);
            }}
            onLayout={search.handleSearchBarLayout}
            style={{ zIndex: 1001, elevation: 1001 }}
          >
            <SearchBar
              value={search.searchQuery}
              onChangeText={search.handleSearchChange}
              placeholder="Search restaurants, dishes, or users"
              onSubmit={actions.handleSearchSubmit}
              onFocus={search.handleSearchFocus}
              onBlur={search.handleSearchBlur}
            />
          </View>

          {checklist.isVisible && !checklist.isLoading && (
            <View className="px-4 pb-2">
              <ProgressiveChecklist
                title="Getting Started"
                items={checklist.items}
                onDismiss={checklist.dismiss}
                onItemPress={(item) => {
                  const def = checklist.items.find((i) => i.id === item.id);
                  if (def?.route) router.push(def.route as any);
                }}
              />
            </View>
          )}

          <View ref={(ref) => registerTarget('discover-cuisine-section', ref)}>
            {data.isCuisinesLoading ? (
              <HorizontalSection title="Categories">
                {CUISINE_SKELETON_KEYS.map((key) => (
                  <CuisineChipSkeleton key={key} />
                ))}
              </HorizontalSection>
            ) : data.cuisines.length > 0 ? (
              <HorizontalSection title="Categories">
                {data.cuisines.map((cuisine) => (
                  <CuisineChip key={cuisine} cuisine={cuisine} />
                ))}
              </HorizontalSection>
            ) : null}
          </View>

          {/* Zone break: the browse cluster (search + categories) gives way to
              the food-content rails with a deliberate extra 8px of air. */}
          <View className="mt-2" ref={(ref) => registerTarget('discover-trending-section', ref)}>
            <RestaurantSection
              title="Trending"
              restaurants={data.trendingBusinesses}
              isLoading={data.isTrendingLoading}
              onSeeAll={actions.handleTrendingSeeAll}
            />
          </View>

          <RestaurantSection
            title="Nearby"
            restaurants={data.nearbyRestaurants}
            isLoading={data.isNearbyLoading}
            onSeeAll={actions.handleNearbySeeAll}
          />

          {/* Social discovery sits below the food content as a secondary nudge. */}
          <SuggestedFriends />

          {data.showEmptyState && <DiscoverEmptyState onAction={actions.handleLeaveReview} />}
        </ScrollView>

        <SearchTypeahead
          showTypeahead={search.showTypeahead}
          searchBarBottom={search.searchBarBottom}
          dropdownTop={search.dropdownTop}
          isLoading={search.isLoading}
          hasResults={search.hasResults}
          hasBusinessResults={search.hasBusinessResults}
          hasUserResults={search.hasUserResults}
          hasMenuItemResults={search.hasMenuItemResults}
          mappedResults={search.mappedResults}
          mappedUserResults={search.mappedUserResults}
          menuItemSearchResults={search.menuItemSearchResults}
          coords={coords}
          units={units}
          onBackdropPress={actions.handleBackdropPress}
          onBusinessPress={actions.handleBusinessPress}
          onUserPress={actions.handleUserPress}
          onMenuItemPress={actions.handleMenuItemPress}
          onMoreResultsPress={actions.handleMoreResultsPress}
        />
        </View>

        <ExistingUserTastePrompt
          visible={existingUserPrompt.isVisible}
          onClose={() => {}}
        />
      </View>
    </TabScreenWrapper>
  );
}
